/**
 * Outsource Partner Portal — Roster Manager.
 * Lets the outsource partner CRUD their own banker roster (Officer + Team
 * Leader entries) without waiting on SAM Rumah admin. Rows here populate
 * the "Assign another banker" dropdown on every case detail page.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Handshake, LogOut, ArrowLeft, Plus, Trash2, Pencil, Loader2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const BLANK = { panel_banker_id: '' };

export default function OutsourceRoster() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [panelBankers, setPanelBankers] = useState([]);
  const [busy, setBusy] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);

  const token = () => localStorage.getItem('outsource_token');
  const authHeaders = () => ({ Authorization: `Bearer ${token()}` });

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [rosterR, panelR] = await Promise.all([
        axios.get(`${API_BASE}/api/outsource/bankers`, { headers: authHeaders() }),
        axios.get(`${API_BASE}/api/outsource/panel-bankers`, { headers: authHeaders() }),
      ]);
      setItems(rosterR.data.items || []);
      setPanelBankers(panelR.data.items || []);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem('outsource_token');
        navigate('/outsource/login');
        return;
      }
      toast.error(e.response?.data?.detail || 'Failed to load roster');
    } finally { setBusy(false); }
  }, [navigate]);

  useEffect(() => {
    if (!token()) { navigate('/outsource/login'); return; }
    load();
  }, [load, navigate]);

  const openCreate = () => { setEditing(null); setForm(BLANK); setShowForm(true); };
  const openEdit = (b) => { setEditing(b); setForm({ panel_banker_id: b.panel_banker_id || '' }); setShowForm(true); };

  const save = async () => {
    if (!form.panel_banker_id) { toast.error('Pick a banker from the panel list'); return; }
    try {
      if (editing) {
        await axios.put(`${API_BASE}/api/outsource/bankers/${editing.id}`, { panel_banker_id: form.panel_banker_id }, { headers: authHeaders() });
        toast.success('Banker updated');
      } else {
        await axios.post(`${API_BASE}/api/outsource/bankers`, { panel_banker_id: form.panel_banker_id }, { headers: authHeaders() });
        toast.success('Banker added');
      }
      setShowForm(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Remove ${b.bank || 'banker'} · ${b.name}?\n\nHistorical case assignments will keep their snapshotted details, but you won't be able to fan future cases to this officer.`)) return;
    try {
      await axios.delete(`${API_BASE}/api/outsource/bankers/${b.id}`, { headers: authHeaders() });
      toast.success('Banker removed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove');
    }
  };

  const logout = () => {
    localStorage.removeItem('outsource_token');
    localStorage.removeItem('outsource_profile');
    navigate('/outsource/login');
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="outsource-roster">
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
              <Handshake className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Outsource Partner Portal</h1>
              <p className="text-xs text-gray-500">Banker Roster</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/outsource/dashboard')} data-testid="outsource-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Button variant="outline" size="sm" onClick={logout} data-testid="outsource-logout-btn">
              <LogOut className="w-4 h-4 mr-1" /> Sign out
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-semibold text-gray-800">My banker roster ({items.length})</h2>
              <p className="text-xs text-gray-500 mt-1">
                Bankers here appear in the &quot;Assign another banker&quot; dropdown on every case you receive. Add or update them anytime.
              </p>
            </div>
            <Button onClick={openCreate} data-testid="outsource-roster-add-btn">
              <Plus className="w-4 h-4 mr-1" /> Add banker
            </Button>
          </div>

          {busy ? (
            <div className="p-8 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-gray-400 text-sm">
              You have no bankers on your roster yet. Click <em>Add banker</em> to register your first officer.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-gray-500">
                  <tr className="border-b">
                    <th className="text-left p-2">Bank</th>
                    <th className="text-left p-2">Branch</th>
                    <th className="text-left p-2">Team Leader</th>
                    <th className="text-left p-2">Officer</th>
                    <th className="text-left p-2">Phone</th>
                    <th className="text-left p-2">Email</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(b => (
                    <tr key={b.id} className="border-b last:border-b-0 hover:bg-slate-50" data-testid={`outsource-roster-row-${b.id}`}>
                      <td className="p-2 font-medium">{b.bank || '—'}</td>
                      <td className="p-2 text-xs">{b.branch || '—'}</td>
                      <td className="p-2 text-xs text-indigo-700">{b.team_leader || '—'}</td>
                      <td className="p-2">{b.name || '—'}</td>
                      <td className="p-2 text-xs">{b.phone || '—'}</td>
                      <td className="p-2 text-xs">{b.email || '—'}</td>
                      <td className="p-2 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(b)} data-testid={`outsource-roster-edit-${b.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(b)} data-testid={`outsource-roster-remove-${b.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Change banker' : 'Add banker from panel'}</DialogTitle>
            <DialogDescription className="text-xs">
              Pick a banker from SAM Rumah&apos;s panel — bank / branch / team leader / phone / email
              are all snapshotted automatically. Need a banker who isn&apos;t on the panel yet? Ask your
              SAM Rumah admin to register them first.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Panel banker *</Label>
              <Select
                value={form.panel_banker_id}
                onValueChange={v => setForm({ panel_banker_id: v })}
              >
                <SelectTrigger data-testid="outsource-roster-panel-select">
                  <SelectValue placeholder={panelBankers.length ? 'Pick a banker' : 'No active panel bankers yet'} />
                </SelectTrigger>
                <SelectContent>
                  {panelBankers.map(pb => (
                    <SelectItem key={pb.id} value={pb.id}>
                      {(pb.bank_code || '—')} · {pb.name}{pb.branch ? ` (${pb.branch})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.panel_banker_id && (() => {
                const pick = panelBankers.find(p => p.id === form.panel_banker_id);
                if (!pick) return null;
                return (
                  <div className="mt-3 p-3 bg-slate-50 rounded-md text-xs space-y-1">
                    <div><span className="text-gray-500">Bank</span> <span className="font-medium">{pick.bank_code}</span></div>
                    <div><span className="text-gray-500">Branch</span> <span className="font-medium">{pick.branch || '—'}</span></div>
                    <div><span className="text-gray-500">Team Leader</span> <span className="font-medium">{pick.team_leader || '—'}</span></div>
                    <div><span className="text-gray-500">Officer</span> <span className="font-medium">{pick.name}</span></div>
                    <div><span className="text-gray-500">Phone</span> <span className="font-medium">{pick.phone || '—'}</span></div>
                    <div><span className="text-gray-500">Email</span> <span className="font-medium">{pick.email || '—'}</span></div>
                  </div>
                );
              })()}
            </div>
            <Button onClick={save} className="w-full" data-testid="outsource-roster-save">{editing ? 'Update' : 'Add'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
